/**
 * Products View
 * Pricing matrix management (tarifs) with Excel import
 */

import React, { useState, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowUpTrayIcon,
  FunnelIcon,
  TableCellsIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import {
  useTarifs,
  useTarifBanques,
  useTarifServices,
  useUploadTarifsExcel,
  useTarifImportBatches,
  useDeleteTarifBatch,
  useDeactivateAllTarifs
} from '../../hooks/useTarifs';
import type { Tarif, TarifFilters, TarifImportResult } from '../../types/facturation.types';
import '../../facturation.css';

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Excel Upload Modal
const ExcelUploadModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<TarifImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useUploadTarifsExcel();
  const deactivateMutation = useDeactivateAllTarifs();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);
    setUploadResult(null);

    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setUploadResult(null);
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file: File) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setError(null);
      // Optionally deactivate all existing tarifs before import
      // await deactivateMutation.mutateAsync();

      const result = await uploadMutation.mutateAsync(selectedFile);
      setUploadResult(result);

      if (result.errorCount === 0) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erreur lors de l\'import');
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    onClose();
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={handleClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Importer les tarifs (Excel)
                    </Dialog.Title>
                    <button
                      onClick={handleClose}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  {/* Drop zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                      ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
                      ${selectedFile ? 'bg-green-50 border-green-300' : ''}
                    `}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    {selectedFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircleIcon className="h-12 w-12 text-green-500" />
                        <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <ArrowUpTrayIcon className="h-12 w-12 text-gray-400" />
                        <p className="text-sm font-medium text-gray-700">
                          Glissez votre fichier Excel ici
                        </p>
                        <p className="text-xs text-gray-500">
                          ou cliquez pour sélectionner (.xlsx, .xls)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                      <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                      <p className="text-sm">{error}</p>
                    </div>
                  )}

                  {/* Upload result */}
                  {uploadResult && (
                    <div className={`p-4 rounded-lg ${uploadResult.errorCount > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {uploadResult.errorCount > 0 ? (
                          <ExclamationCircleIcon className="h-5 w-5 text-yellow-600" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        )}
                        <span className={`font-medium ${uploadResult.errorCount > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                          Import terminé
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <p>Tarifs importés: <strong>{uploadResult.totalImported}</strong></p>
                        <p>Erreurs: <strong>{uploadResult.errorCount}</strong></p>
                      </div>
                      {uploadResult.errors.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-600 mb-1">Détails des erreurs:</p>
                          <ul className="text-xs text-gray-600 max-h-32 overflow-auto">
                            {uploadResult.errors.map((err, i) => (
                              <li key={i} className="py-0.5">
                                {err.sheet} / {err.service} / {err.prestation}: {err.error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Format info */}
                  <div className="text-xs text-gray-500">
                    <p className="font-medium mb-1">Format attendu:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>1 onglet par banque (PRIVE, Belfius, etc.)</li>
                      <li>Colonnes: Code, Prestation, FULL EXPERTISE, DRIVE-BY, DESKTOP, REOUVERTURE, REVISION</li>
                      <li>Chaque service a 2 colonnes: H Bureaux (prix), H Expert</li>
                    </ul>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    {uploadResult ? 'Fermer' : 'Annuler'}
                  </button>
                  {!uploadResult && (
                    <button
                      onClick={handleUpload}
                      disabled={!selectedFile || uploadMutation.isPending}
                      className="fact-btn-upload"
                    >
                      {uploadMutation.isPending ? 'Import en cours...' : 'Importer'}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Import Batches Modal (for admin management)
const ImportBatchesModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { data: batches, isLoading, refetch } = useTarifImportBatches();
  const deleteMutation = useDeleteTarifBatch();

  const handleDelete = async (batchId: string) => {
    if (!confirm('Supprimer tous les tarifs de cet import ?')) return;

    try {
      await deleteMutation.mutateAsync(batchId);
      refetch();
    } catch (err: any) {
      // Extract error message from axios error response
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur inconnue';
      const usedTarifs = err?.response?.data?.data?.usedTarifs;

      if (usedTarifs && usedTarifs.length > 0) {
        // Show detailed message about which tarifs are in use
        const tarifList = usedTarifs.slice(0, 5).map((t: any) => `• ${t.name} (${t.banque})`).join('\n');
        const moreMsg = usedTarifs.length > 5 ? `\n... et ${usedTarifs.length - 5} autres` : '';
        alert(`${errorMessage}\n\nTarifs utilisés:\n${tarifList}${moreMsg}`);
      } else {
        alert(`Erreur: ${errorMessage}`);
      }
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Historique des imports
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {isLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    </div>
                  ) : !batches || batches.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucun import trouvé</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-auto">
                      {batches.map((batch) => (
                        <div key={batch.import_batch_id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(batch.imported_at).toLocaleDateString('fr-BE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {batch.tarif_count} tarifs • {batch.banques.join(', ')}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDelete(batch.import_batch_id)}
                              disabled={deleteMutation.isPending}
                              className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                              title="Supprimer cet import"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    Fermer
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const ProductsView: React.FC = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBatchesModal, setShowBatchesModal] = useState(false);
  const [selectedBanque, setSelectedBanque] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');

  // Fetch tarifs with filters
  const filters: TarifFilters = {
    banqueCode: selectedBanque || undefined,
    serviceCode: selectedService || undefined,
    activeOnly: true
  };
  const { data: tarifs, isLoading, refetch } = useTarifs(filters);
  const { data: banques } = useTarifBanques();
  const { data: services } = useTarifServices();

  // Group tarifs by banque for matrix display (when no filters)
  const groupedByBanque = React.useMemo(() => {
    if (!tarifs) return {};

    const groups: Record<string, Tarif[]> = {};
    tarifs.forEach(tarif => {
      if (!groups[tarif.banque_code]) {
        groups[tarif.banque_code] = [];
      }
      groups[tarif.banque_code].push(tarif);
    });
    return groups;
  }, [tarifs]);

  const handleUploadSuccess = () => {
    refetch();
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Banque filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={selectedBanque}
                onChange={(e) => setSelectedBanque(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary min-w-[160px]"
              >
                <option value="">Toutes les banques</option>
                {banques?.map((b) => (
                  <option key={b.banque_code} value={b.banque_code}>
                    {b.banque_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Service filter */}
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary min-w-[160px]"
            >
              <option value="">Tous les services</option>
              {services?.map((s) => (
                <option key={s.service_code} value={s.service_code}>
                  {s.service_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowBatchesModal(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Historique
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="facturation-btn-primary"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Importer Excel
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : !tarifs || tarifs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <TableCellsIcon className="h-16 w-16 mb-4" />
            <p className="text-lg font-medium">Aucun tarif trouvé</p>
            <p className="text-sm mt-1">
              {selectedBanque || selectedService
                ? 'Modifiez les filtres ou '
                : ''}
              Importez un fichier Excel pour commencer
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="fact-btn-upload mt-4"
            >
              Importer des tarifs
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {!selectedBanque && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Banque
                  </th>
                )}
                {!selectedService && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prestation
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix HTVA
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix TVAC
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hon. Expert (TVAC)
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TVA
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tarifs.map((tarif) => (
                <tr key={tarif.id} className="hover:bg-gray-50">
                  {!selectedBanque && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {tarif.banque_name}
                      </span>
                    </td>
                  )}
                  {!selectedService && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {tarif.service_name}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm font-mono text-gray-600">
                      {tarif.prestation_code}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">
                      {tarif.prestation_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(tarif.price_htva)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="text-sm text-gray-700">
                      {formatCurrency(tarif.price_tvac)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="text-sm text-gray-500">
                      {tarif.honoraire_expert_htva
                        ? formatCurrency(tarif.honoraire_expert_htva)
                        : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className="text-sm text-gray-500">
                      {tarif.tva_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary */}
      <div className="px-6 py-4 bg-white border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {tarifs?.length || 0} tarif{(tarifs?.length || 0) !== 1 ? 's' : ''}
            {selectedBanque && banques && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                {banques.find(b => b.banque_code === selectedBanque)?.banque_name}
              </span>
            )}
            {selectedService && services && (
              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                {services.find(s => s.service_code === selectedService)?.service_name}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            TVA: 21% par défaut
          </div>
        </div>
      </div>

      {/* Modals */}
      <ExcelUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={handleUploadSuccess}
      />
      <ImportBatchesModal
        isOpen={showBatchesModal}
        onClose={() => setShowBatchesModal(false)}
      />
    </div>
  );
};

export default ProductsView;
